<template>
  <transition name="decided">
    <article
      :data-state="cookieState.view"
      :data-reopened="reopened"
      class="Banner"
    >
      <header class="Header">
        {{ $t('cookies.text') }}
        <router-link :to="{ path: privacyRoute }" class="Privacy_Link">{{
          $t('cookies.privacy_policy_underline')
        }}</router-link>
      </header>
      <div
        :style="{ '--learn-more-width': `${width}px` }"
        class="LearnMoreWrapper"
        ref="learnMoreRef"
      >
        <button
          @click="send({ event: 'toggle', payload: null })"
          class="LearnMoreBtn"
        >
          <div>
            <span class="MoreInfoText">{{ $t('cookies.collapse_link') }}</span>
            <span :data-arrow="cookieState.arrow" class="arrow" />
          </div>
        </button>
        <transition name="learnmore">
          <p v-if="cookieState.showMore" class="LearnMoreText">
            {{ $t('cookies.collapse_text') }}
          </p>
        </transition>
      </div>
      <div class="ButtonWrapper" ref="buttonRef">
        <button
          class="Decline"
          @click="
            send({
              event: 'decline',
              payload: null,
            })
          "
        >
          {{ $t('cookies.button_deny') }}
        </button>
        <button
          class="Accept"
          @click="
            send({
              event: 'accept',
              payload: null,
            })
          "
        >
          {{ $t('cookies.button_accept') }}
        </button>
      </div>
    </article>
  </transition>
</template>

<script>
import i18n from '../../i18n';

export default {
  name: 'CookieBanner',
  props: ['send', 'cookieState', 'reopened'],
  data() {
    return { privacyRoute: `/${i18n.locale}/privacy`, width: 0 };
  },
  mounted() {
    if (this.$refs.learnMoreRef) {
      // Measure learnmore's width and assign it to a css variable
      // This is necessary to find correct width when applying absolute positioning of child element
      const { offsetWidth } = this.$refs.learnMoreRef;
      this.width = offsetWidth;
    }
  },
};
</script>

<style scoped>
.Banner {
  z-index: 100;
  --banner-f-size: 11px;
  --banner-height: 176px;
  --banner-padding: 0 1.5em;
  --btn-black: black;
  --btn-white: white;
  --border-radius: 24px;
  --btn-padding: 0;
  --btn-height: 28px;
  --btn-width: 145px;
  --btn-text-size: 12px;
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  background: #000000;
  padding: var(--banner-padding);
  transition: height 0.35s ease-in-out;
  animation: enterScreen 0.35s 1s ease-in-out backwards;
  display: grid;
  grid-template-columns: 1;
  grid-template-rows: max-content minmax(82px, max-content);
}

.Header {
  color: white;
  font-size: var(--banner-f-size);
  margin-bottom: 1.5em;
  margin-top: 1.5em;
  grid-row: 1;
}
.LearnMoreBtn {
  color: #7dcc93;
  font-size: var(--banner-f-size);
  font-weight: normal;
  outline: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 130px;
}
.Accept,
.Decline {
  height: var(--btn-height);
  width: var(--btn-width);
  font-size: var(--btn-text-size);
  padding: var(--btn-padding);
  border-radius: var(--border-radius);
}
.Accept {
  color: var(--btn-black);
  background: var(--btn-white);
}
.Decline {
  color: var(--btn-white);
  background: var(--btn-black);
  border: 2px solid var(--btn-white);
}
.LearnMoreWrapper {
  grid-row: 2;
}

.LearnMoreText {
  margin-top: 0.5em;
  color: white;
  font-size: var(--banner-f-size);
  transition: height 0.35s ease-in-out;
  width: var(--learn-more-width);
}
.Privacy_Link {
  text-decoration: underline;
}
.MoreInfoText {
  margin-right: 0.5em;
}
.arrow {
  border: solid #7dcc93;
  width: 7px;
  height: 7px;
  border-width: 0 2px 2px 0;
  display: inline-block;
  transition: transform 0.25s ease-in-out;
}

.ButtonWrapper {
  width: 100%;
  align-self: flex-end;
  justify-self: center;
  flex: 1;
  display: flex;
  justify-content: space-evenly;
  height: 60px;
  position: absolute;
  bottom: 0;
}

.Grid {
  display: grid;
  grid-template-columns: 80fr 20fr;
}

/* ANIMATIONS */

/* Reopened from the footer: appear at once, the delay is for first visits. */
.Banner[data-reopened='true'] {
  animation-delay: 0s;
}

.Banner[data-state='small'] {
  height: var(--banner-height);
}

.Banner[data-state='big'] {
  height: 275px;
}

.arrow[data-arrow='down'] {
  transform: rotate(45deg);
}
.arrow[data-arrow='up'] {
  transform: rotate(-135deg);
}

/* Cookie Banner entrance animation  */
@keyframes enterScreen {
  from {
    transform: translateY(150%);
  }
  to {
    transform: translateY(0%);
  }
}

/* VUE TRANSITIONS */

/* Apply position absolute during anmation to stop child element layout issues */
.learnmore-enter-active,
.learnmore-leave-active {
  transition: opacity 0.35s, height 0.3s linear;
  position: absolute;
  width: var(--learn-more-width);
}
.learnmore-enter,
.learnmore-leave-to {
  opacity: 0;
  height: 0px;
  position: absolute;
  width: var(--learn-more-width);
}
/* Exit animation for cookie banner */
.decided-leave-active {
  animation: exit 0.5s ease-in-out;
}
@keyframes exit {
  to {
    transform: translateY(130%);
  }
}

/* Medium to large screen sizes */

@media (min-width: 768px) {
  .Banner {
    --banner-f-size: 14px;
  }
}
@media (min-width: 1024px) {
  .Banner {
    --banner-f-size: 14px;
    --banner-padding: 0 100px;
    --banner-height: 184px;
    --btn-black: black;
    --btn-white: white;
    --border-radius: 24px;
    --btn-padding: 0;
    --btn-height: 32px;
    --btn-width: 130px;
    --btn-text-size: 15px;
    grid-template-columns: 60fr 40fr;
    grid-template-rows: minmax(80px, auto) 1fr;
  }
  .LearnMoreWrapper {
    grid-column: 1;
  }
  .Header {
    grid-column: 1 / -1;
  }
  .ButtonWrapper {
    grid-column: 2;
    margin-bottom: 2em;
    height: auto;
    position: relative;
  }
  .LearnMoreText {
    height: 100px;
  }
  .Banner[data-state='big'] {
    height: 320px;
  }
  .MoreInfoText {
    margin-right: 0.5em;
  }
  .arrow {
    border-width: 0 2px 2px 0;
    width: 12px;
    height: 12px;
  }
}

@media (min-width: 1200px) {
  .Banner {
    --banner-padding: 0 120px;
    --banner-f-size: 15px;
    --btn-text-size: 15px;
    grid-template-columns: 70fr 30fr;
  }
}
@media (min-width: 1400px) {
  .Banner {
    --banner-padding: 0 120px;
    --banner-f-size: 15px;
    --btn-text-size: 15px;
    grid-template-columns: 70fr 30fr;
  }
}
@media (min-width: 1600px) {
  .Banner {
    --banner-padding: 0 150px;
    --banner-f-size: 18px;
    --btn-text-size: 16px;
    --btn-height: 32px;
    --btn-width: 150px;
    grid-template-columns: 70fr 30fr;
  }
}
@media (min-width: 1800px) {
  .Banner {
    --banner-padding: 0 200px;
    --banner-f-size: 20px;
    --btn-text-size: 18px;
    --btn-height: 42px;
    --btn-width: 160px;
    grid-template-columns: 70fr 30fr;
  }
}
</style>
